import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Close as CloseIcon } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { ApprovalsService } from 'api-clients/monolith';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import globalMessages from 'shared/global/messages';

import messages from './messages';
import { useStyles } from './styles';
import { ApprovalRule, OFFER } from './types';

export interface DeleteRuleDialogProps {
  rule: ApprovalRule;
  /** How many in-flight approvals this delete would cancel (AXHE-4018). */
  pendingRequests: number;
  /** True while that count is still being fetched. */
  pendingLoading: boolean;
  onCancel: () => void;
  onDeleted: () => void;
}

type DeleteResponse = Awaited<
  ReturnType<typeof ApprovalsService.deleteInternalApiApprovalsRules>
>;

const TITLE_ID = 'delete-rule-title';

const bold = (chunks: React.ReactNode) => <b>{chunks}</b>;

/**
 * Confirmation for deleting a rule.
 *
 * A centred modal rather than the editor's drawer: this is a single, reversible-
 * by-nobody decision with no data entry, which is exactly what a modal is for.
 *
 * Built on MUI rather than the FDS `Modal`/`InlineMessage`/`Button` shells, per
 * review on #4366 — this package is not taking new `@fountain/fountain-ui-components`
 * surface area. MUI's `Dialog` is what FDS's `Modal` wraps, so nothing is lost
 * on that swap: `role="dialog"`, the focus trap and the scroll lock are `Dialog`'s
 * own. The chrome FDS added on top — the close x, the header and footer rules,
 * the tinted footer band — is restated here against theme tokens.
 *
 * It names two consequences, not one. The cascade — the rule *and* its levels —
 * comes from `level_count`, so a collapsed row that never loaded its tree still
 * states the real number. The second is what the design's copy left out and
 * AXHE-4018 answered: deleting a rule cancels the approvals it was gating, so
 * the offers or openings sitting in someone's queue stop being held by it. An
 * admin who deletes a rule without being told that is discovering it from the
 * affected candidates.
 */
export const DeleteRuleDialog: React.FC<DeleteRuleDialogProps> = ({
  rule,
  pendingRequests,
  pendingLoading,
  onCancel,
  onDeleted,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const theme = useTheme();
  // The one thing `Modal` did for free that a bare `Dialog` does not. At 448px
  // the dialog is wider than the smallest phones, and a modal that overflows
  // puts its own actions off the edge.
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const destroy = useCallback(
    () => ApprovalsService.deleteInternalApiApprovalsRules(rule.id as string),
    [rule.id],
  );
  const options = useMemo(() => ({ onSuccess: onDeleted }), [onDeleted]);
  const { result, mutation } = useApiServiceMutation<DeleteResponse, typeof destroy>(
    destroy,
    options,
  );

  return (
    // No `maxWidth={false}`, which FDS's shell passed. `paperWidthSm`'s 600px
    // cap is live again here and deliberately left that way: it cannot bind at
    // 448px, and below 600px this is `fullScreen` anyway. Not an oversight.
    <Dialog
      aria-labelledby={TITLE_ID}
      classes={{ paper: styles.dialogPaper }}
      fullScreen={fullScreen}
      onClose={onCancel}
      open
    >
      <DialogTitle className={styles.dialogTitle} disableTypography>
        <Typography id={TITLE_ID} variant="subtitle2">
          <FormattedMessage {...messages.deleteRuleTitle} />
        </Typography>
        {/* Named, not "close-button". The dismiss affordance is the one control
            here a screen-reader user reaches before the copy explains itself. */}
        <IconButton
          aria-label={intl.formatMessage(globalMessages.close)}
          className={styles.dialogClose}
          onClick={onCancel}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* `dividers` is the rule under the header and above the footer — the
          same two lines FDS's ModalContent drew, straight from MUI. */}
      <DialogContent className={styles.dialogContent} dividers>
        <Typography variant="body2" className={styles.dialogSentence}>
          <FormattedMessage
            {...messages.deleteRuleCascade}
            values={{
              b: bold,
              name: rule.name,
              count: rule.level_count ?? rule.levels?.length ?? 0,
            }}
          />
        </Typography>

        {/* Only when there are any. "0 approvals will be cancelled" is noise
            that trains people to skip the line that matters.

            The count arrives after the dialog is already open, so this callout
            genuinely appears late — which is the case `Alert`'s own role="alert"
            is for, and why it is left on rather than suppressed. */}
        {pendingRequests > 0 && (
          <Alert className={styles.dialogCallout} severity="warning">
            {intl.formatMessage(messages.deleteRulePending, {
              b: bold,
              count: pendingRequests,
              // Which noun the consequence names. One rule table backs both the
              // Offers and Openings filters, so a rule deleted from the openings
              // view must not be described as releasing offers.
              //
              // `subject_type` is optional on the generated type — every field
              // in the OpenAPI document is — and ICU would quietly take an
              // undefined through to `other`, i.e. "offers". Defaulted the way
              // `ruleForm.draftFor` defaults the same field, so the fallback is
              // stated rather than being whichever branch ICU happens to reach.
              subject: rule.subject_type ?? OFFER,
            })}
          </Alert>
        )}

        <Typography variant="body2" className={styles.dialogFootnote}>
          <FormattedMessage {...messages.deleteRuleUndone} />
        </Typography>

        {/* One message for every failure, deliberately. The one status worth
            distinguishing is the 409 from `reject_legacy_managed!`, and that is
            unreachable from here: `RuleRow` renders no delete action for a
            legacy-managed rule. If that ever drifts, this is the line that has
            to grow a status-specific branch — the server's "managed on the
            Opening Approvals page" reason is more useful than "try again". */}
        {result.status === 'error' && (
          <Typography variant="body2" className={styles.dialogError} role="alert">
            <FormattedMessage {...messages.deleteFailed} />
          </Typography>
        )}
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button className={styles.dialogButton} onClick={onCancel} variant="outlined">
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
        {/* Outlined rather than filled, and held until the pending count has
            actually loaded, so nobody confirms a cascade whose size the dialog
            has not yet told them. A destructive action never carries the
            surface's primary weight.

            Labelled with the action rather than a bare "Delete": the same
            string the row's own trigger uses, which is what the standard means
            by a destructive button matching its title. The prototype's label is
            the bare verb — AXHE-4553 holds that open. */}
        <Button
          className={styles.dialogDeleteButton}
          disabled={pendingLoading || result.status === 'loading'}
          onClick={() => mutation()}
          variant="outlined"
        >
          <FormattedMessage {...messages.deleteRule} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
